import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar, User, Share2 } from 'lucide-react';
import { useAnnouncement } from '@/hooks/useApi';

export default function AnnouncementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: announcement, isLoading, error } = useAnnouncement(id || '');

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-bg-secondary rounded w-1/4" />
          <div className="h-10 bg-bg-secondary rounded w-3/4" />
          <div className="h-60 bg-bg-secondary rounded" />
          <div className="space-y-3">
            <div className="h-4 bg-bg-secondary rounded" />
            <div className="h-4 bg-bg-secondary rounded w-5/6" />
            <div className="h-4 bg-bg-secondary rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !announcement) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-text-secondary mb-4">{t('common.error')}</p>
        <Link to="/announcements" className="btn-primary">{t('common.backToList')}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link
        to="/announcements"
        className="inline-flex items-center gap-1 text-primary text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <ArrowLeft className="w-4 h-4" /> {t('common.backToList')}
      </Link>

      <span className="badge text-xs mb-3 block w-fit">{announcement.category}</span>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">{announcement.title}</h1>

      <div className="flex items-center gap-4 text-sm text-text-secondary mb-8">
        <span className="flex items-center gap-1">
          <Calendar className="w-4 h-4" /> {announcement.createdAt}
        </span>
        <span className="flex items-center gap-1">
          <User className="w-4 h-4" /> {announcement.author}
        </span>
        <button className="flex items-center gap-1 hover:text-primary transition-colors ml-auto">
          <Share2 className="w-4 h-4" /> {t('common.share')}
        </button>
      </div>

      {announcement.imageUrl && (
        <div className="w-full rounded-lg overflow-hidden mb-8">
          <img src={announcement.imageUrl} alt="" className="w-full h-auto" />
        </div>
      )}

      <div
        className="prose prose-lg max-w-none text-text-primary"
        dangerouslySetInnerHTML={{ __html: announcement.content }}
      />
    </div>
  );
}
